import React from "react";
import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

export function SectionCard({ children, className = "", accent = false, ...props }) {
  return (
    <div
      className={`bg-white rounded-2xl border border-brand-line p-5 sm:p-6 shadow-[0_4px_20px_rgba(184,169,154,0.08)] ${
        accent ? "relative overflow-hidden" : ""
      } ${className}`}
      {...props}
    >
      {accent && (
        <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-brand-blush/30 blur-2xl" />
      )}
      <div className="relative">{children}</div>
    </div>
  );
}

export function SectionTitle({ children, sub }) {
  return (
    <div className="mb-4">
      <h3 className="font-serif text-xl font-medium text-brand-anthracite">{children}</h3>
      {sub && <p className="mt-1 text-sm text-brand-taupe">{sub}</p>}
    </div>
  );
}

export function Label2({ children, className = "" }) {
  return (
    <span className={`text-xs font-medium uppercase tracking-wider text-[#8C827A] ${className}`}>{children}</span>
  );
}

export function InfoBox({ title, children, tone = "neutral" }) {
  const tones = {
    neutral: "bg-brand-sand/60 border-brand-line text-brand-anthracite",
    blush: "bg-brand-blush/30 border-brand-taupe/30 text-brand-anthracite",
    success: "bg-[#4E7C59]/10 border-[#4E7C59]/25 text-[#3c5f45]",
    warning: "bg-[#C78238]/10 border-[#C78238]/30 text-[#8a5a24]",
    danger: "bg-[#B85B5B]/10 border-[#B85B5B]/30 text-[#8a4141]",
  };
  return (
    <div className={`rounded-xl border p-4 text-sm leading-relaxed ${tones[tone]}`} data-testid="info-box">
      {title && <div className="mb-1 font-semibold">{title}</div>}
      <div>{children}</div>
    </div>
  );
}

export function InfoTooltip({ text }) {
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" className="inline-flex text-brand-taupe hover:text-brand-anthracite" data-testid="info-tooltip">
            <Info className="h-4 w-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs bg-brand-anthracite text-white text-xs leading-relaxed">
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function PillGroup({ options, value, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((o) => {
        const active = String(value) === String(o.value);
        return (
          <button
            key={o.value}
            type="button"
            data-testid={`${testid}-${o.value}`}
            onClick={() => onChange(o.value)}
            className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-all ${
              active
                ? "border-brand-champagne bg-brand-champagne/25 text-brand-anthracite"
                : "border-brand-line bg-white text-brand-taupe hover:border-brand-taupe"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function ResultRow({ label, value, strong = false, tone }) {
  const toneColor = tone === "success" ? "text-[#4E7C59]" : tone === "danger" ? "text-[#B85B5B]" : "text-brand-anthracite";
  return (
    <div className="flex items-center justify-between py-2">
      <span className={`text-sm ${strong ? "font-medium text-brand-anthracite" : "text-brand-taupe"}`}>{label}</span>
      <span className={`font-sans tabular-nums ${strong ? "text-lg font-semibold" : "text-sm font-medium"} ${toneColor}`}>
        {value}
      </span>
    </div>
  );
}
